<template>
  <div>
    
    <div class="tr" :class="{even: index % 2 === 0}">

      <div class="left" @click="goLeft()">
        <img src="@/assets/images/left-arrow.png">
      </div>

      <div class="data-item" v-if="curSection === 'info'">
        <div class="fw-bold">{{baseStation.lrrRef}}
          <span class="badge bg-success" v-if="baseStation.cnxState === 'CNX'">{{baseStation.cnxState}}</span>
          <span class="badge bg-danger" v-if="baseStation.cnxState === 'DISC'">{{baseStation.cnxState}}</span>
        </div>
        <div :title="baseStation.name">{{formatName(baseStation.name)}}</div>
        <div>
          <span class="text-dark small"><font-awesome-icon icon="mobile-alt"/> {{baseStation.commercialName}} / {{baseStation.version}}</span>
        </div>
        <div><span class="text-dark small">{{baseStation.supplierId}}</span></div>
      </div>

      <div class="data-item" v-if="curSection === 'connection'">
        <div class="data-section">
          <div class="badge text-uppercase">connection</div>
          <span class="badge bg-success" v-if="baseStation.cnxState === 'CNX'">{{baseStation.cnxState}}</span>
          <span class="badge bg-danger" v-if="baseStation.cnxState === 'DISC'">{{baseStation.cnxState}}</span>
        </div>
        
        <div class="data-section">
          <div class="badge text-uppercase">last traffic</div>
          <div>
            <div>{{formatTs(baseStation.cnxStateSince).time}}</div>
            <div>{{formatTs(baseStation.cnxStateSince).ago}}</div>
          </div>
        </div>
      </div>

      <div class="data-item" v-if="curSection === 'healthTraffic'">
        <div class="data-section">
          <div class="badge text-uppercase">health state</div>
          <span class="badge bg-danger" v-if="baseStation.healthState.includes('ERROR')">{{baseStation.healthState}}</span>
          <span class="badge bg-primary" v-if="!baseStation.healthState.includes('ERROR')">{{baseStation.healthState}}</span>
        </div>
        
        <div class="data-section">
          <div class="badge text-uppercase">last uplink/downlink</div>
          <div><font-awesome-icon icon="upload"/> {{formatTs(baseStation.lastUpFrame).time}}</div>
          <div><font-awesome-icon icon="download"/> {{formatTs(baseStation.lastDwFrame).time}}</div>
        </div>

      </div>

      <div class="data-item" v-if="curSection === 'backhauls'">
        <div class="data-section">
          <div class="badge text-uppercase">backhauls</div>
          
          <div v-for="conn in baseStation.connList" :key="conn.name">

            <span v-if="conn.state === 'DOWN'" class="text-dark small">{{conn.name}} | {{conn.type}} | <span class="badge bg-danger">{{conn.state}}</span> | <font-awesome-icon icon="upload"/> avg {{conn.txAvgRate}} | <font-awesome-icon icon="download"/> avg {{conn.rxAvgRate}}</span>
            <span v-if="conn.state === 'UP'" class="text-dark small">{{conn.name}} | {{conn.type}} | <span class="badge bg-primary">{{conn.state}}</span> | <font-awesome-icon icon="upload"/> avg {{conn.txAvgRate}} | <font-awesome-icon icon="download"/> avg {{conn.rxAvgRate}}</span>
          </div>
        </div>
      </div>

      <div class="data-item" v-if="curSection === 'location'">
        <div class="data-section">
          <div class="badge text-uppercase">location</div>

          <div v-if="baseStation.location">
            <div><span class="badge px-0">LAT {{baseStation.location.lat}}</span></div>
            <div><span class="badge px-0">LONG {{baseStation.location.long}}</span></div>
            <div>
            <a
:href="`https://www.google.com/maps/search/?api=1&query=${baseStation.location.lat},${baseStation.location.long}`"
              target="_blank"
              class="cp" 
              data-tippy-content="open in Google Maps"
>

              <font-awesome-icon icon="external-link-alt"/>
            </a>
            </div>
          </div>

          <div v-if="!baseStation.location">
            <span class="text-dark small">N/A</span>
          </div>
          
        </div>
      </div>

      <div class="right" @click="goRight()">
        <img src="@/assets/images/right-arrow.png">
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import dayjs from '@/plugins/dayjs'
import tippy from 'tippy.js'
import type { BaseStation } from '@/types'

interface Props {
  baseStation: BaseStation
  index: number
}

withDefaults(defineProps<Props>(), {
  baseStation: () => ({} as BaseStation),
  index: 0
})

const curSection = ref<'info'|'connection'|'healthTraffic'|'backhauls'|'location'>('info')

onMounted(() => {
  tippy('[data-tippy-content]')
})

const formatTs = (ts: number | undefined): { time: string, ago: string } => {
  return {
    time: ts ? dayjs(ts).format('L LT') : 'N/A',
    ago: ts ? dayjs(ts).fromNow() : 'N/A'
  }
}

const formatName = (name: string): string => {
  if (name.length <= 30)
    return name
  return name.substring(0, 27) + '...'
}

const goLeft = () => {
  switch(curSection.value) {
    case 'info': curSection.value = 'location'; break;
    case 'connection': curSection.value = 'info'; break;
    case 'healthTraffic': curSection.value = 'connection'; break;
    case 'backhauls': curSection.value = 'healthTraffic'; break;
    case 'location': curSection.value = 'backhauls'; break;
  }
}

const goRight = () => {
  switch(curSection.value) {
    case 'info': curSection.value = 'connection'; break;
    case 'connection': curSection.value = 'healthTraffic'; break;
    case 'healthTraffic': curSection.value = 'backhauls'; break;
    case 'backhauls': curSection.value = 'location'; break;
    case 'location': curSection.value = 'info'; break;
  }
}
</script>

<style scoped lang="scss">
@use "@/variables.scss" as *;

.tr {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: stretch;
  height: 120px;
}

.tr.even {
  background-color: $bgColourLight;
}

.left, .right {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
}

.data-item {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  margin-bottom: 10px;
}

.data-section {
  display: flex;
  flex-direction: column;
  margin-top:3px;
  margin-bottom:3px;
}

</style>